import type {
  AccessoInput,
  EsitoAccesso,
  NuovoDispositivoInput,
  RegistrazioneInput,
  ReinviaCodiceInput,
  UtenteCorrente,
  VerificaEmailInput,
} from '@aulaviva/shared';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { ErroreRichiesta, richiesta } from './client';

export const chiaveMe = ['me'] as const;

export async function leggiUtenteCorrente(): Promise<UtenteCorrente | null> {
  try {
    const { dati } = await richiesta<UtenteCorrente>('/me');
    return dati;
  } catch (e) {
    if (e instanceof ErroreRichiesta && e.status === 401) return null;
    throw e;
  }
}

export function queryMe(client: QueryClient) {
  return client.fetchQuery({ queryKey: chiaveMe, queryFn: leggiUtenteCorrente, staleTime: 60_000 });
}

export function useMe() {
  return useQuery({ queryKey: chiaveMe, queryFn: leggiUtenteCorrente, staleTime: 60_000 });
}

export function useRegistrazione() {
  return useMutation({
    mutationFn: async (input: RegistrazioneInput) => (await richiesta<{ email: string }>('/auth/registrazione', { method: 'POST', json: input })).dati,
  });
}

export function useVerificaEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: VerificaEmailInput) =>
      (await richiesta<{ esito: 'ok'; utente: UtenteCorrente }>('/auth/verifica-email', { method: 'POST', json: input })).dati,
    onSuccess: ({ utente }) => qc.setQueryData(chiaveMe, utente),
  });
}

export function useAccesso() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AccessoInput) => (await richiesta<EsitoAccesso>('/auth/accesso', { method: 'POST', json: input })).dati,
    onSuccess: (esito) => {
      if (esito.esito === 'ok') qc.setQueryData(chiaveMe, esito.utente);
    },
  });
}

export function useNuovoDispositivo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NuovoDispositivoInput) =>
      (await richiesta<{ esito: 'ok'; utente: UtenteCorrente }>('/auth/nuovo-dispositivo', { method: 'POST', json: input })).dati,
    onSuccess: ({ utente }) => qc.setQueryData(chiaveMe, utente),
  });
}

export function useReinviaCodice() {
  return useMutation({
    mutationFn: async (input: ReinviaCodiceInput) => (await richiesta<unknown>('/auth/reinvia-codice', { method: 'POST', json: input })).status,
  });
}

export function useEsci() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await richiesta<unknown>('/auth/esci', { method: 'POST' })).status,
    onSettled: () => {
      qc.setQueryData(chiaveMe, null);
      void qc.invalidateQueries({ queryKey: chiaveMe });
    },
  });
}

/** Secondo passo dell'accesso per gli amministratori con TOTP attivo. */
export function useAccessoTotp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; codice: string }) =>
      (await richiesta<{ esito: 'ok'; utente: UtenteCorrente }>('/auth/totp', { method: 'POST', json: input })).dati,
    onSuccess: ({ utente }) => qc.setQueryData(chiaveMe, utente),
  });
}
